import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { sentInvitationsService } from '../services/api';

const SentInvitations = () => {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  
  const { user } = useSelector((state) => state.auth) || {};

  const statusFilters = [
    { key: 'all', label: 'All Invitations', color: 'text-gray-600' },
    { key: 'sent', label: 'Sent', color: 'text-blue-600' },
    { key: 'accepted', label: 'Accepted', color: 'text-green-600' },
    { key: 'declined', label: 'Declined', color: 'text-red-600' },
    { key: 'expired', label: 'Expired', color: 'text-gray-500' }
  ];

  const inviteTypeLabels = {
    'player': 'Player',
    'staff': 'Staff Member',
    'agent': 'Agent'
  };

  useEffect(() => {
    fetchSentInvitations();
  }, [activeFilter]);

  const fetchSentInvitations = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = activeFilter !== 'all' ? { status: activeFilter } : {};
      const response = await sentInvitationsService.getSentInvitations(params);
      
      if (response.success) {
        setInvitations(response.data || []);
      } else {
        setError(response.error || 'Failed to fetch sent invitations');
      }
    } catch (error) {
      console.error('Error fetching sent invitations:', error);
      setError('An unexpected error occurred while fetching invitations');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      sent: { label: 'Sent', className: 'bg-blue-100 text-blue-800' },
      accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800' },
      declined: { label: 'Declined', className: 'bg-red-100 text-red-800' },
      expired: { label: 'Expired', className: 'bg-gray-100 text-gray-800' }
    };

    const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">Error</div>
          <div className="text-gray-600">{error}</div>
          <button
            onClick={fetchSentInvitations}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className=" mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Sent Invitations</h1>
          <p className="text-gray-600">Track all invitations you have sent</p>
        </div>

        {/* Status Filters */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          {/* The five filters are wider than a 390 viewport. They now scroll
              inside their own container instead of dragging the page body into
              horizontal scroll ("Declin…" was cut off the right edge) — the
              same strip idiom as ViewPitchesTabs (T018), including
              overflow-y-hidden, because overflow-x:auto coerces overflow-y to
              auto and would otherwise grow a phantom vertical scrollbar. The
              underline styling is deliberately left as authored: per D16 the
              tab/pill families are not swapped while fixing an overflow. */}
          <div className="flex flex-nowrap border-b overflow-x-auto overflow-y-hidden">
            {statusFilters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => setActiveFilter(filter.key)}
                className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-none ${
                  activeFilter === filter.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        {/* Invitations List */}
        {invitations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m0 0V9a2 2 0 012-2h8a2 2 0 012 2v4M6 13v4a2 2 0 002 2h8a2 2 0 002-2v-4" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invitations found</h3>
            <p className="text-gray-500">
              {activeFilter === 'all' 
                ? "You haven't sent any invitations yet."
                : `No ${activeFilter} invitations found.`
              }
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 table-sticky-first">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Invitee
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Sent Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Response Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {invitation.invitee_name || invitation.email}
                          </div>
                          <div className="text-sm text-gray-500">
                            {invitation.email}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {inviteTypeLabels[invitation.invite_type] || invitation.invite_type}
                        </div>
                        {invitation.role_type && (
                          <div className="text-sm text-gray-500">
                            {invitation.role_type.replace('_', ' ')}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(invitation.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(invitation.sent_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(invitation.responded_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SentInvitations; 